import { useCallback, useEffect, useMemo } from 'react';
import {
  type UVInsertConfig,
  type UVInsertFaceElement,
  isUvInsertUserConfigurableText,
} from '@shared/models/uv-inserts';
import {
  getFaceElementsForRegionTab,
  getUvInsertFaceTabItems,
  getImageUploadPropertyKeysForRegionTab,
} from '@shared/view/uv-inserts/visualiser-tabs';
import { getLiquidProductVariantImageSrc } from '@shared/view/uv-inserts/liquid-variant-image';
import { useUvInsertsContext } from '../context/UvInsertsContext';
import { VISUALISER_ROUTE_META } from '../route-meta';

type UseVisualiserPageViewModelOptions = {
  /** Current route name — used to look up config handle and productKey. */
  routeName: string;
  /** UserInputContext — used to set visualiserSelection for visualiserCartService. */
  userInput: {
    setVisualiserSelection: (options: {
      productKey: string | null;
      variant: unknown | null;
      config?: { mapping: { text?: Record<string, string>; images?: Record<string, string> } } | null;
    }) => void;
    clearVisualiserSelection: () => void;
    setTextInput: (key: string, value: string) => void;
    getTextInput: (key: string) => string;
    setImageInput: (key: string, value: unknown) => void;
  };
};

/**
 * Builds a cart-compatible config.mapping from a UVInsertConfig's faceElements.
 * Since lineItemPropertyKey IS the cart property name, the mapping is identity
 * (e.g. { UVFrontText: 'UVFrontText' }), which lets visualiserCartService work unchanged.
 * @param {UVInsertConfig} config - The UV insert config to build the mapping from.
 * @returns {{ text: Record<string, string>, images: Record<string, string> }} The mapping.
 */
function buildCartMappingFromConfig(config: UVInsertConfig): {
  text: Record<string, string>;
  images: Record<string, string>;
} {
  const text: Record<string, string> = {};
  const images: Record<string, string> = {};

  for (const face of config.faceElements) {
    for (const el of face.elementConfig) {
      if (el.type === 'TEXT') {
        if (!isUvInsertUserConfigurableText(el) || !el.lineItemPropertyKey) continue;
        text[el.lineItemPropertyKey] = el.lineItemPropertyKey;
      } else if (el.type === 'IMAGE_UPLOAD') {
        if (!el.lineItemPropertyKey) continue;
        images[el.lineItemPropertyKey] = el.lineItemPropertyKey;
      }
    }
  }

  return { text, images };
}

/**
 * ViewModel for UvInsertVisualiserPage. Reads from UvInsertsContext, derives
 * all display data, and wires UserInputContext.setVisualiserSelection on mount.
 */
export function useUvInsertVisualiserPageViewModel({
  routeName,
  userInput,
}: UseVisualiserPageViewModelOptions) {
  const {
    resolved,
    activeConfig,
    setActiveConfig,
    activeTabIndex,
    setActiveTabIndex,
    themeIndex,
    textByKey,
    setText,
    imageByKey,
    setImageByKey,
    imageTransformByKey,
    setImageTransformByKey,
  } = useUvInsertsContext();

  const routeMeta = VISUALISER_ROUTE_META[routeName];

  // Resolve config by handle from resolved configs.
  const config = useMemo<UVInsertConfig | null>(() => {
    if (!resolved || !routeMeta) return null;
    return resolved.configs.find((c) => c.handle === routeMeta.configHandle) ?? null;
  }, [resolved, routeMeta]);

  // Set/clear active config in UvInsertsContext on mount/unmount.
  useEffect(() => {
    setActiveConfig(config);
    return () => {
      setActiveConfig(null);
    };
  }, [config, setActiveConfig]);

  // The selected theme variant (used for background image and cart variant).
  const safeThemeIndex = config ? Math.min(Math.max(themeIndex, 0), config.themes.length - 1) : 0;
  const selectedVariant = config?.themes[safeThemeIndex] ?? null;

  // Stable references to avoid stale closures in effects.
  const { setVisualiserSelection, clearVisualiserSelection, setTextInput, setImageInput } = userInput;

  // Wire UserInputContext.setVisualiserSelection so visualiserCartService can build cart items.
  // Also re-fires when selectedVariant changes (themeIndex change).
  useEffect(() => {
    if (!config || !routeMeta) {
      clearVisualiserSelection();
      return;
    }

    const mapping = buildCartMappingFromConfig(config);
    setVisualiserSelection({
      productKey: routeMeta.productKey,
      variant: selectedVariant,
      config: { mapping },
    });

    return () => {
      clearVisualiserSelection();
    };
  }, [config, selectedVariant, routeMeta, setVisualiserSelection, clearVisualiserSelection]);

  // Sync textByKey to UserInputContext so visualiserCartService.getTextInput() works.
  useEffect(() => {
    for (const [key, value] of Object.entries(textByKey)) {
      setTextInput(key, value);
    }
  }, [textByKey, setTextInput]);

  // Derived tab items.
  const faceTabItems = useMemo(
    () => (config ? getUvInsertFaceTabItems({ faceElements: config.faceElements }) : []),
    [config]
  );

  // Faces to render for the active tab.
  const facesToShow = useMemo<UVInsertFaceElement[]>(
    () =>
      config
        ? getFaceElementsForRegionTab({ faceElements: config.faceElements, tabIndex: activeTabIndex })
        : [],
    [config, activeTabIndex]
  );

  // Image upload keys for the active tab (used to show FileInput controls).
  const imageUploadKeysForActiveTab = useMemo<string[]>(
    () =>
      config
        ? getImageUploadPropertyKeysForRegionTab({
            faceElements: config.faceElements,
            tabIndex: activeTabIndex,
          })
        : [],
    [config, activeTabIndex]
  );

  const selectedThemeImageSrc = selectedVariant
    ? getLiquidProductVariantImageSrc({ variant: selectedVariant })
    : null;

  const handleImageSelect = useCallback(
    (key: string, payload: unknown) => {
      // Store File for display in VisualiserImageArea.
      const p = payload as { userImage?: { file?: File | null } | null };
      const file = p?.userImage?.file instanceof File ? p.userImage.file : null;
      setImageByKey((prev) => ({ ...prev, [key]: file }));

      // Store full payload in UserInputContext for visualiserCartService.
      setImageInput(key, payload);
    },
    [setImageByKey, setImageInput]
  );

  return {
    config,
    resolved,
    faceTabItems,
    facesToShow,
    activeTabIndex,
    setActiveTabIndex,
    selectedThemeImageSrc,
    textByKey,
    setText,
    imageByKey,
    /** Raw dispatch — passed directly to DevFaceVisualisers which uses the setter pattern internally. */
    setImageTransformByKey,
    imageTransformByKey,
    imageUploadKeysForActiveTab,
    handleImageSelect,
  };
}
